import { useState, ReactElement } from "react"
import {
  faEye,
  faTrash,
  faPenToSquare,
  faSquareCaretDown,
  IconDefinition,
  faBoxArchive,
  faRotateLeft,
  faCircleExclamation,
} from "@fortawesome/free-solid-svg-icons"
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { ContextMenu, ContextMenuItem, Modal, Pagination, Search, Button, Checkbox } from "@wfp/ui"
import BaseTable, { AutoResizer, Column, ColumnShape } from "react-base-table"
import { useNavigate } from "react-router-dom"
import { useForm } from "react-hook-form"
import { SimpleSelect, XlsForm } from "interfaces/dataInterfaces"
import { GET_ITEM_LIMIT, HH_MODULE } from "constants/constants"
import {
  useGetXlsFormQuery,
  useDeleteXlsFormSchemaMutation,
  useArchiveXlsHHMutation,
} from "services/household/xlsFormSchemas"
import { checkAccess, formatDate } from "utils/helpers"
import useCommon from "hooks/useCommon"
import { useAppSelector } from "hooks/hooksStore"
import { selectUser } from "features/common/commonSlice"
import HeaderCTA from "components/Shared/HeaderFilters/HeaderCTA"
import HeaderFilters from "components/Shared/HeaderFilters/HeaderFilters"
import CountrySelector from "components/Shared/HeaderFilters/AssociatedCountriesSelector"
import PermissionWrapper from "components/Shared/Common/PermissionWrapper"

interface IContextItem {
  onAction: () => void
  feature: string
  label: string
  icon: IconDefinition
  color: string
}

type Inputs = {
  name: string
  localName: string
}

const permissionsProps = {
  moduleId: HH_MODULE,
  subModule: "xlsForms",
}

const XlsFormList = (): ReactElement => {
  const navigate = useNavigate()
  const { showSnackBar } = useCommon()
  const user = useAppSelector(selectUser)

  const [pageNumber, setPageNumber] = useState<number>(1)
  const [xlsFormsIsArchived, setxlsFormsIsArchived] = useState<boolean>(false)
  const [getItemLimit, setGetItemLimit] = useState<number>(GET_ITEM_LIMIT)
  useForm<Inputs>()
  const [actionItem, setActionItem] = useState<{ item: XlsForm; action: "delete" } | null>(null)
  const [admCode, setAdmCode] = useState<number>(0)
  const [searchValue, setSearchValue] = useState<string>("")
  const [query, setQuery] = useState<string>("")

  const { data: xlsData } = useGetXlsFormQuery({
    adm0Code: admCode,
    search: query,
    skip: pageNumber === 1 ? 0 : (pageNumber - 1) * getItemLimit,
    take: getItemLimit,
  })
  const [deleteForm] = useDeleteXlsFormSchemaMutation()
  const [archive] = useArchiveXlsHHMutation()

  const handleDelete = async (id: number): Promise<void> => {
    try {
      await deleteForm(id).unwrap()
      setActionItem(null)
      showSnackBar("XLSForm deleted successfully", "success")
    } catch (err) {
      const error = err as FetchBaseQueryError
      showSnackBar((error?.data as { message: string })?.message ?? "Failed to delete the XLSForm", "error")
    }
  }

  const handleArchive = async (xlsFormId: number, archived: boolean): Promise<void> => {
    try {
      await archive({ xlsFormId, setArchived: !archived }).unwrap()

      showSnackBar(`XLSForm ${archived ? "restored" : "archived"} successfully`, "success")
    } catch (error) {
      showSnackBar(`Failed to ${archived ? "restore" : "archive"} the XLSForm `, "error")
    }
  }

  const handleSearch = (): void => {
    setQuery(searchValue)
    setPageNumber(1)
  }

  const getData = (event?: SimpleSelect): void => {
    setAdmCode(event?.value || 0)
    setPageNumber(1)
  }

  const RenderContextItem = ({ onAction, feature, label, icon, color }: IContextItem) => {
    return (
      <PermissionWrapper {...permissionsProps} feature={feature}>
        <ContextMenuItem className="hover:bg-slate-200 p-1.5 px-2 text-left w-28" onClick={onAction}>
          <div>
            <FontAwesomeIcon icon={icon} color={color} className="mr-1" />
            <span style={{ color }}>{label}</span>
          </div>
        </ContextMenuItem>
      </PermissionWrapper>
    )
  }

  const getActionsMenu = (row: XlsForm) => [
    {
      label: "Details",
      icon: faEye,
      color: "var(--interactive-01)",
      feature: "details",
      isVisible: () => true,
      onAction: (rowData: XlsForm) => navigate(`${rowData.xlsFormId}#details`),
    },
    {
      label: "Map",
      icon: faPenToSquare,
      color: "var(--support-02)",
      feature: "edit",
      isVisible: (rowData: XlsForm) => rowData.isValid,
      onAction: (rowData: XlsForm) => navigate(`${rowData.xlsFormId}#mapping`),
    },
    {
      label: row.xlsFormsIsArchived ? "Restore" : "Archive",
      icon: row.xlsFormsIsArchived ? faRotateLeft : faBoxArchive,
      color: row.xlsFormsIsArchived ? "var(--interactive-01)" : "var(--text-02)",
      feature: "edit",
      isVisible: () => true,
      onAction: (rowData: XlsForm) => handleArchive(rowData.xlsFormId, rowData.xlsFormsIsArchived),
    },
    {
      label: "Delete",
      icon: faTrash,
      color: "var(--support-01)",
      feature: "delete",
      isVisible: () => true,
      onAction: (data: XlsForm) => {
        setActionItem({
          item: {
            ...data,
          },
          action: "delete",
        })
      },
    },
  ]

  const columns: Array<ColumnShape<XlsForm>> = [
    {
      key: "",
      dataKey: "",
      title: "",
      width: 20,
      minWidth: 20,
      className: "!p-1",
      hidden: xlsData?.items.every(item => item.isValid),
      cellRenderer: ({ rowData }) => (
        <div>
          {!rowData.isValid && (
            <FontAwesomeIcon icon={faCircleExclamation} color="var(--support-01)" title="Invalid XLSForm" />
          )}
        </div>
      ),
    },
    {
      key: "xlsFormId",
      dataKey: "xlsFormId",
      title: "ID",
      width: 80,
    },
    {
      key: "countryName",
      dataKey: "countryName",
      title: "Country",
      width: 135,
      minWidth: 100,
      resizable: true,
    },
    {
      key: "xlsFormName",
      dataKey: "xlsFormName",
      title: "XLSForm Name",
      width: 200,
      flexGrow: 1,
      resizable: true,
    },
    {
      key: "baseXlsFormName",
      dataKey: "baseXlsFormName",
      title: "Base XLSForm Name",
      width: 200,
      resizable: true,
      minWidth: 100,
    },
    {
      key: "xlsFormCreateDate",
      // dataKey: "xlsFormCreateDate",
      title: "Created Date",
      width: 150,
      cellRenderer: ({ rowData }) => formatDate(rowData.xlsFormCreateDate),
    },
    {
      key: "userName",
      dataKey: "userName",
      title: "Created By",
      width: 250,
      minWidth: 200,
      resizable: true,
    },
    {
      key: "action",
      title: "Action",
      width: 80,
      align: Column.Alignment.CENTER,
      hidden: !checkAccess(user?.roles, [permissionsProps.moduleId], permissionsProps.subModule, "all"),
      cellRenderer: ({ rowData }) => (
        <div
          style={{
            minWidth: "2rem",
            minHeight: "2rem",
            borderRadius: "50%",
          }}
          onClick={e => {
            e.preventDefault()
            e.stopPropagation()
          }}
        >
          <ContextMenu
            content={getActionsMenu(rowData).map(
              item =>
                item.isVisible(rowData) && (
                  <RenderContextItem
                    key={item.label}
                    label={item.label}
                    feature={item.feature}
                    icon={item.icon}
                    color={item.color}
                    onAction={() => item.onAction(rowData)}
                  />
                ),
            )}
          >
            <div style={{ padding: "0.7rem 1rem" }}>
              <FontAwesomeIcon icon={faSquareCaretDown} color="var(--interactive-01)" size="xl" />
            </div>
          </ContextMenu>
        </div>
      ),
    },
  ]

  return (
    <>
      <HeaderFilters
        leftWidth="w-9/12"
        left={
          <>
            <CountrySelector onSelect={getData} className="w-5/12 mr-2" />
            <Search
              id="search"
              onChange={e => setSearchValue(e.toString())}
              className="mr-1"
              style={{ borderRadius: 5 }}
              // @ts-ignore
              closeButtonLabelText="Clear search input"
            />
            <Button onClick={handleSearch}>Search</Button>

            <Checkbox
              wrapperClassName="mx-4 !mb-0 max-w-fit"
              labelText="Show archived"
              onChange={() => {
                setxlsFormsIsArchived(prev => !prev)
                setPageNumber(1)
              }}
              checked={xlsFormsIsArchived}
              value="xlsFormsIsArchived"
              id="xlsFormsIsArchived"
            />
          </>
        }
        right={
          <PermissionWrapper {...permissionsProps} feature="addNew">
            <HeaderCTA onClick={() => navigate("add")} label="Add new XLSForm schema" />
          </PermissionWrapper>
        }
      />

      <div style={{ height: "calc(100vh - 225px)", minHeight: "60vh" }} className="mt-1">
        <AutoResizer>
          {({ width, height }) => (
            <BaseTable
              data={xlsData?.items}
              width={width}
              height={height}
              rowKey="xlsFormId"
              className="custom-table clickable"
              rowHeight={50}
              headerHeight={45}
              columns={columns}
              emptyRenderer={() => <p>No records to display</p>}
              rowEventHandlers={{
                onClick: row => {
                  navigate(`/household/xlsFormSchema/${row.rowKey}#details`)
                },
              }}
            />
          )}
        </AutoResizer>

        <Modal
          lazyLoad
          danger
          open={actionItem?.action === "delete"}
          modalHeading="Confirm Delete"
          primaryButtonText="Delete"
          secondaryButtonText="Cancel"
          onRequestSubmit={() => handleDelete(actionItem!.item.xlsFormId)}
          onRequestClose={() => setActionItem(null)}
        >
          Are you sure you want to delete the selected XLSForm?
        </Modal>
      </div>

      <Pagination
        totalItems={xlsData?.count ?? 0}
        pageSize={getItemLimit}
        pageSizes={[10, 20, 30]}
        page={pageNumber}
        onChange={event => {
          const item = event as unknown as { page: number; pageSize: number }
          setPageNumber(item?.page)
          setGetItemLimit(item?.pageSize)
        }}
      />
    </>
  )
}

export default XlsFormList
